import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["$theme"];
import UISortTH from 'UIComponents/table/UISortTH';
import styled from 'styled-components';
import { useCallback, useMemo, useRef, useEffect, Fragment, memo } from 'react';
import { makeGetColumnWidth, setWidth } from '../../resizing/redux/widthsSlice';
import { useTableDispatch, useTableSelector } from '../../redux/reduxHooks';
import UIDragHandleHoverContainer from 'UIComponents/draggable/UIDragHandleHoverContainer';
import { getContentComponent } from './getContentComponent';
import { getColumnKey } from '../../table/utils/getColumnKey';
import { CELL_BORDER_WIDTH } from '../../table/constants/sizes';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useContextMenuHandler } from '../../contextMenu/context/ContextMenuHandlerContext';
import { useTrellisTheme } from '../../utils/useTrellisTheme';

// HACK: This weird wrapper is purely to ensure that we don't pass the extra props
// down to the underlying UISortTH. If we upgraded styled-components to support either
// transient props or the ignoreProps api we could use that, but sadly our repackage
// does not support it.
import { jsx as _jsx } from "react/jsx-runtime";
const StripOutThemePropUISortTH = /*#__PURE__*/memo(_ref => {
  let rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(UISortTH, Object.assign({}, rest));
});
const ResizableUISortTH = styled(StripOutThemePropUISortTH).withConfig({
  displayName: "ResizableSortTH__ResizableUISortTH",
  componentId: "tu0h2h-0"
})(["box-shadow:inset 0 -", "px ", ";overflow-wrap:anywhere;word-break:normal;&:last-of-type{width:auto !important;}"], CELL_BORDER_WIDTH, ({
  $theme
}) => $theme.color['border-primary-subtle']);
const getSortFromDirection = sortDirection => {
  if (sortDirection === -1) {
    return 'ascending';
  }
  if (sortDirection === 1) {
    return 'descending';
  }
  return 'none';
};
const getDirectionFromSort = sort => sort === 'ascending' ? -1 : 1;
const ResizableSortTH = ({
  column,
  sortDirection,
  columnIndex,
  columnPosition,
  isLastColumn,
  numberOfFrozenColumns,
  onColumnWidthChange,
  onColumnOrderChange,
  onSortChange,
  children
}) => {
  const $theme = useTrellisTheme();
  const {
    columnName,
    columnNamespace,
    sortable,
    width: columnWidth,
    minimumColumnWidth
  } = column;
  const columnKey = getColumnKey(columnNamespace, columnName);
  const getColumnWidth = useMemo(() => makeGetColumnWidth(columnKey), [columnKey]);
  const width = useTableSelector(getColumnWidth);
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);
  const resizableTHStyle = useMemo(() => ({
    width,
    backgroundColor: $theme.color['fill-surface-raised']
  }), [width, $theme]);
  const stickyResizableTHStyle = useMemo(() => ({
    width,
    backgroundColor: $theme.color['fill-surface-raised'],
    borderRight: numberOfFrozenColumns - 1 === columnIndex ? `${$theme.borderWidth['300']} solid ${$theme.color['border-core-subtle']}` : `1px solid ${$theme.color['border-core-default']}`,
    left: `${columnPosition}px`,
    position: 'sticky',
    zIndex: 2
  }), [columnIndex, columnPosition, numberOfFrozenColumns, width, $theme]);
  const styles = useMemo(() => numberOfFrozenColumns > columnIndex ? stickyResizableTHStyle : resizableTHStyle, [columnIndex, numberOfFrozenColumns, resizableTHStyle, stickyResizableTHStyle]);

  // We must use a ref in this callback because handleMouseMove in getContent above
  // captures the first version it sees. We keep this ref in sync with component
  // state via useEffect. This is when we "commit" the new width.
  const onResizeEnd = useCallback(() => {
    onColumnWidthChange({
      columnNamespace,
      columnName,
      nextWidth: widthRef.current
    });
  }, [columnNamespace, columnName, onColumnWidthChange]);
  const dispatch = useTableDispatch();
  const handleResize = useCallback(nextWidth => dispatch(setWidth({
    columnKey,
    width: Math.max(nextWidth, minimumColumnWidth)
  })), [columnKey, dispatch, minimumColumnWidth]);
  const handleSortChange = useCallback(({
    target: {
      value
    }
  }) => onSortChange({
    columnNamespace,
    columnName,
    direction: getDirectionFromSort(value)
  }), [onSortChange, columnName, columnNamespace]);
  const Content = useMemo(() => getContentComponent({
    column,
    originalWidth: columnWidth,
    columnIndex,
    isLastColumn,
    handleResize,
    onResizeEnd,
    onColumnOrderChange
  }), [column, columnIndex, isLastColumn, columnWidth, handleResize, onColumnOrderChange, onResizeEnd]);

  // This id is part of our external api and should not be changed.
  // It will be used for features like coaching tips and onboarding tours.
  const externalId = `header-${columnNamespace}-${columnName}`;

  // HACK: These selectors are required for compatibility with onboarding tours
  // and coaching tips. When we've renamed the contact "name" column to it's
  // more permanent placeholder name, we should remove this and PR onboarding
  // tours/coaching tips to use the standardaized selectors.
  const isContact = columnNamespace === CONTACT_TYPE_ID;
  const coachingTipsSelectors = isContact && columnName === 'name' ? {
    'data-coaching-tips': 'header-name'
  } : {};
  const onboardingSelectors = isContact ? {
    'data-onboarding': `crm-table-header-cell-${columnName}`
  } : {};
  const handleContextMenu = useContextMenuHandler({
    columnNamespace,
    columnName
  });
  return /*#__PURE__*/_jsx(UIDragHandleHoverContainer, {
    children: /*#__PURE__*/_jsx(ResizableUISortTH, Object.assign({}, coachingTipsSelectors, onboardingSelectors, {
      "data-table-external-id": externalId,
      "data-test-id": externalId,
      "data-test-sort-direction": sortDirection,
      className: "p-all-0",
      role: "columnheader",
      disabled: !sortable,
      style: styles,
      FlexContainer: Fragment,
      Content: Content,
      sort: getSortFromDirection(sortDirection),
      onSortChange: handleSortChange
      // @ts-expect-error this is a valid prop
      ,
      onContextMenu: handleContextMenu,
      $theme: $theme,
      children: children
    }))
  });
};
export default ResizableSortTH;